/**
 * This is a leaner implementation of @components/Icon.tsx created
 * for the buy flow, which doesn't use the same responsive breakpoints
 * within its icon components.
 *
 * To set the icon size responsively, override the `font-size` property
 * using an extended Styled Component.
 *
 * i.e. styled(BuyFlowIcon)`font-size: 16px`
 */

import { colours, mediaQuery, pxToRem } from "@helpers";
import React from "react";
import styled from "styled-components";

export const DEFAULT_CONTAINER_SIZE = 36; // what the design file has as a container size
const SVG_VIEWBOX_SIZE = 22; // what the SVGs (used to make the icon font file) have as their viewbox dimension

/**
 * Based on the provided `iconName` returns if there is a corresponding
 * SVG in the additional icons added for the buy flow.
 *
 * @param {string} iconName
 * @returns {string | null}
 */
const getIconSvgSrc = (iconName) => {
	return `/icons/${iconName}.svg`;
};

const IconI = styled.i`
  width: ${DEFAULT_CONTAINER_SIZE}px;
  height: ${DEFAULT_CONTAINER_SIZE}px;
  font-size: ${DEFAULT_CONTAINER_SIZE}px;

  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    font-size: ${SVG_VIEWBOX_SIZE / DEFAULT_CONTAINER_SIZE}em !important;
    ${(props) => (props.color ? `color: ${props.color} !important;` : "")}
  }
`;

const IconImage = styled.img`
  width: ${pxToRem(DEFAULT_CONTAINER_SIZE)};
  height: auto;

  ${mediaQuery.tabletAndUp} {
    width: ${pxToRem(DEFAULT_CONTAINER_SIZE, "tablet")};
  }
`;

/**
 * Icon component for the buy flow.
 */
const BuyFlowIcon = ({
	iconName,
	color = colours.MIDNIGHT_BLUE,
	className = "",
	alt = "",
	...rest
}) => {
	const iconSvgSrc = getIconSvgSrc(iconName);
	const IconSrc = getIconSvgSrc(iconName);

	return (
		<>
			{iconSvgSrc ? (
				<IconImage {...rest} alt={alt} className={className} src={IconSrc} />
			) : (
				<IconI
					{...rest}
					className={`icon-${iconName}-felix ${className}`}
					color={color}
				/>
			)}
		</>
	);
};

export default BuyFlowIcon;
